import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { Form, Formik } from "formik";
import { Button } from "@volca/ui/components/ui/button";
import { Label } from "@volca/ui/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@volca/ui/components/ui/sheet";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { toast } from "@volca/ui/lib/toast";
import { useBusiness } from "@/core/business";
import { formatDateRange } from "@/core/utils/date";
import type { EnrichedIcmStatementDetail } from "@/icm/types";
import { useSubmitTechFeedback } from "@/tech-feedback/query";
import { useGetMyUser } from "@/user/query";
import {
  buildFeedbackInquiry,
  getFeedbackValidationSchema,
  getTechnicianDisplayName,
} from "./utils";

export const TechnicianFeedbackSheet = ({
  open,
  onOpenChange,
  statement,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  statement: EnrichedIcmStatementDetail | null;
}) => {
  const { selectedBusiness } = useBusiness();
  const { data: techUser } = useGetMyUser();
  const { mutate: submitFeedback, isPending } = useSubmitTechFeedback();

  if (!techUser) return null;

  const displayName = getTechnicianDisplayName(techUser);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="overflow-hidden rounded-t-2xl sm:mx-auto sm:max-h-[90vh] sm:max-w-[500px] sm:rounded-2xl sm:border"
        showCloseButton={false}
      >
        <Stack align="start" gap={0} className="flex h-full w-full flex-col">
          <SheetTitle className="sr-only">Dispute This Statement</SheetTitle>
          <SheetDescription className="sr-only">
            Submit a dispute about this pay statement
          </SheetDescription>
          <Stack align="start" gap={4} className="w-full p-6">
            <Stack
              className="w-full"
              gap={4}
              direction="row"
              justify="between"
              align="start"
            >
              <Text variant="heading">Dispute This Statement</Text>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Close dispute sheet"
                onClick={() => onOpenChange(false)}
              >
                <XIcon />
              </Button>
            </Stack>
            <Text tone="muted">
              Think something looks off? Describe what you believe is wrong and
              we'll review it. Your statement details will be attached
              automatically.
            </Text>
            <Stack className="w-full" align="start" gap={0}>
              <Text variant="label" tone="muted">
                Sending as
              </Text>
              <Text>{displayName}</Text>
              <Text variant="caption" tone="muted">
                {techUser.email}
              </Text>
              {statement && (
                <Text variant="caption" tone="muted">
                  {selectedBusiness?.name} ·{" "}
                  {formatDateRange(
                    statement.period_start,
                    statement.period_end,
                  )}
                </Text>
              )}
            </Stack>
          </Stack>

          <div className="w-full flex-1 overflow-hidden">
            <div className="h-full w-full overflow-y-auto">
              <Formik
                initialValues={{ inquiry: "" }}
                validationSchema={getFeedbackValidationSchema({
                  statement,
                  businessName: selectedBusiness?.name,
                })}
                validateOnBlur={false}
                onSubmit={(values, { resetForm }) => {
                  submitFeedback(
                    {
                      name: displayName,
                      email: techUser.email,
                      inquiry: buildFeedbackInquiry({
                        inquiry: values.inquiry,
                        statement,
                        businessName: selectedBusiness?.name,
                      }),
                    },
                    {
                      onSuccess: () => {
                        toast.success("Dispute submitted.", {
                          description:
                            "We'll review your statement and follow up.",
                        });
                        resetForm();
                        onOpenChange(false);
                      },
                      onError: (error) => {
                        toast.error("Failed to submit feedback", {
                          description:
                            error.errors?.join(", ") ||
                            "Please try again later.",
                        });
                      },
                    },
                  );
                }}
              >
                {({ getFieldProps, errors, touched }) => (
                  <Form className="w-full">
                    <Stack
                      align="start"
                      gap={6}
                      className="w-full px-6 pt-5 pb-16"
                    >
                      <Stack className="w-full" align="start" gap={1}>
                        <Stack gap={2} className="h-52 w-full">
                          <Label htmlFor="dispute-inquiry">
                            What looks wrong? <span aria-hidden>*</span>
                          </Label>
                          <Textarea
                            id="dispute-inquiry"
                            className="min-h-32"
                            placeholder="Describe what you think is incorrect or missing. *"
                            {...getFieldProps("inquiry")}
                          />
                        </Stack>
                        {touched.inquiry && errors.inquiry && (
                          <Text className="text-danger">{errors.inquiry}</Text>
                        )}
                      </Stack>
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={isPending}
                        loading={isPending}
                      >
                        Submit Dispute
                      </Button>
                    </Stack>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </Stack>
      </SheetContent>
    </Sheet>
  );
};
